import React, { useEffect, useRef } from 'react';
import { Typography } from 'antd';
import Countdown from 'react-countdown';
import moment from 'moment';
import { startServerTimeSync, getServerNow } from '../../utils/serverTime';

const { Text } = Typography;

const AuctionCountdown = ({
    auction,
    onCountdownComplete,
    variant = 'overlay', // 'overlay', 'inline', 'detailed', 'compact'
    showIcon = true,
    className = '',
    style = {}
}) => {
    const hasCalledComplete = useRef(false);

    useEffect(() => {
        startServerTimeSync();
    }, []);

    const getTimeLeft = (auction) => {
        const start = moment(auction.start_time);
        const timeUntilStart = moment.duration(start.diff(moment(getServerNow())));
        
        // If auction hasn't started yet, show countdown
        if (timeUntilStart.asMilliseconds() > 0) {
            if (timeUntilStart.asDays() > 1) {
                return `${Math.floor(timeUntilStart.asDays())}d ${Math.floor(timeUntilStart.asHours() % 24)}h ${Math.floor(timeUntilStart.asMinutes() % 60)}m ${Math.floor(timeUntilStart.asSeconds() % 60)}s`;
            } else if (timeUntilStart.asHours() > 1) {
                return `${Math.floor(timeUntilStart.asHours())}h ${Math.floor(timeUntilStart.asMinutes() % 60)}m ${Math.floor(timeUntilStart.asSeconds() % 60)}s`;
            } else {
                return `${Math.floor(timeUntilStart.asMinutes())}m ${Math.floor(timeUntilStart.asSeconds() % 60)}s`;
            }
        }
        
        // Auction has started - show status based on auction.status
        if (auction.status === 'completed') {
            return 'Completed';
        } else if (auction.status === 'active') {
            return 'LIVE NOW';
        } else {
            return 'Started';
        }
    };

    const getTimeLeftColor = (auction) => {
        const start = moment(auction.start_time);
        const timeUntilStart = moment.duration(start.diff(moment(getServerNow())));
        
        // If auction hasn't started yet, show countdown colors
        if (timeUntilStart.asMilliseconds() > 0) {
            const hoursLeft = timeUntilStart.asHours();
            
            if (hoursLeft < 1) {
                return '#dc2626'; // Red for less than 1 hour
            } else if (hoursLeft < 6) {
                return '#f59e0b'; // Orange for less than 6 hours
            } else {
                return '#059669'; // Green for more than 6 hours
            }
        }
        
        // Auction has started - show green for active/completed
        return '#10b981'; // Green for started
    };

    const renderCountdown = ({ days, hours, minutes, seconds, completed }) => {
        if (completed) {
            // Only call the callback once, even if renderer is called multiple times
            if (onCountdownComplete && !hasCalledComplete.current) {
                hasCalledComplete.current = true;
                onCountdownComplete();
            }
            return (
                <Text style={{
                    color: getTimeLeftColor(auction),
                    fontWeight: 700,
                    fontSize: variant === 'detailed' ? '16px' : variant === 'compact' ? '12px' : '14px',
                    textShadow: variant === 'overlay' ? '0 1px 2px rgba(0, 0, 0, 0.5)' : 'none'
                }}>
                    {(auction.status === 'active' || auction.status === 'live')
                        ? 'LIVE NOW'
                        : auction.status === 'completed'
                            ? 'Completed'
                            : 'Started'}
                </Text>
            );
        }

        let timeString;
        if (variant === 'compact') {
            // Compact format for small cards - more dynamic updates
            if (days > 0) {
                timeString = `${days}d ${hours}h`;
            } else if (hours > 0) {
                timeString = `${hours}h ${minutes}m`;
            } else if (minutes >= 10) {
                timeString = `${minutes}m`;
            } else {
                // Show seconds when less than 10 minutes for more dynamic updates
                timeString = `${minutes}m ${seconds}s`;
            }
        } else {
            // Full format for other variants
            timeString = days > 0 
                ? `${days}d ${hours}h ${minutes}m ${seconds}s`
                : hours > 0 
                    ? `${hours}h ${minutes}m ${seconds}s`
                    : `${minutes}m ${seconds}s`;
        }

        return (
            <Text style={{
                color: getTimeLeftColor(auction),
                fontWeight: 700,
                fontSize: variant === 'detailed' ? '16px' : variant === 'compact' ? '12px' : '14px',
                textShadow: variant === 'overlay' ? '0 1px 2px rgba(0, 0, 0, 0.5)' : 'none'
            }}>
                {showIcon && variant !== 'compact' && '⏰ '}{timeString}
            </Text>
        );
    };

    const getContainerStyle = () => {
        const baseStyle = {
            ...style
        };

        switch (variant) {
            case 'overlay':
                return {
                    ...baseStyle,
                    position: 'absolute',
                    top: 16,
                    right: 10,
                    zIndex: 10,
                    background: 'rgba(0, 0, 0, 0.8)',
                    borderRadius: '20px',
                    padding: '8px 16px',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                };
            case 'inline':
                return {
                    ...baseStyle,
                    display: 'inline-block',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    background: 'rgba(255, 255, 255, 0.9)',
                    border: '1px solid rgba(0, 0, 0, 0.1)',
                };
            case 'detailed':
                return {
                    ...baseStyle,
                    padding: '12px 20px',
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, rgba(24, 144, 255, 0.1), rgba(24, 144, 255, 0.05))',
                    border: '1px solid rgba(24, 144, 255, 0.2)',
                    textAlign: 'center',
                };
            case 'compact':
                return {
                    ...baseStyle,
                    position: 'absolute',
                    bottom: 8,
                    right: 8,
                    zIndex: 10,
                    padding: '4px 8px',
                    borderRadius: '8px',
                    background: 'rgba(0, 0, 0, 0.8)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                };
            default:
                return baseStyle;
        }
    };

    // This countdown always reflects the *scheduled* start_time, regardless
    // of status - an owner can flip status to active/live before start_time
    // (that only controls whether "Enter Auction" is available, handled
    // elsewhere), but this display should keep counting down until the
    // scheduled time actually arrives, using the synced server clock.
    const hasStarted = moment(getServerNow()).isAfter(moment(auction.start_time));

    if (hasStarted) {
        return (
            <div style={getContainerStyle()} className={className}>
                <Text style={{
                    color: getTimeLeftColor(auction),
                    fontWeight: 700,
                    fontSize: variant === 'detailed' ? '16px' : variant === 'compact' ? '12px' : '14px',
                    textShadow: variant === 'overlay' ? '0 1px 2px rgba(0, 0, 0, 0.5)' : 'none'
                }}>
                    {(auction.status === 'active' || auction.status === 'live')
                        ? 'LIVE NOW'
                        : auction.status === 'completed'
                            ? 'Completed'
                            : 'Started'}
                </Text>
            </div>
        );
    }

    return (
        <div style={getContainerStyle()} className={className}>
            <Countdown
                date={moment(auction.start_time).valueOf()}
                now={getServerNow}
                renderer={renderCountdown}
                // Don't pass onComplete here - we handle it in renderCountdown to avoid duplicate calls
            />
        </div>
    );
};

export default AuctionCountdown; 